import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, doc, getDoc, setDoc, updateDoc, deleteDoc, getDocs } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faSave, faTimes, faEye, faEyeSlash, faPlus } from '@fortawesome/free-solid-svg-icons';
import './AdminTemplates.css';
import { useAdmin } from '../contexts/AdminContext';

const AdminTemplates = () => {
  const { isAdmin, isLoading: adminLoading } = useAdmin();
  const [defaultTemplates, setDefaultTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editIndex, setEditIndex] = useState(null);
  const [editName, setEditName] = useState('');
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [editTemplateIndex, setEditTemplateIndex] = useState(null);
  const [editSegmentIndex, setEditSegmentIndex] = useState(null);
  const [editSegmentName, setEditSegmentName] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [newTemplateName, setNewTemplateName] = useState('');
  const [showNewTemplateForm, setShowNewTemplateForm] = useState(false);

  // Use a ref to track if we've already fetched templates
  const hasFetchedRef = React.useRef(false);

  // Fetch default templates from Firestore
  useEffect(() => {
    // Skip if still loading admin status
    if (adminLoading) {
      return;
    }

    // Check admin status
    if (!isAdmin) {
      setError('Only admins can manage default templates');
      setLoading(false);
      return;
    }

    // Prevent multiple fetches
    if (hasFetchedRef.current) {
      return;
    }

    const fetchDefaultTemplates = async () => {
      try {
        hasFetchedRef.current = true;
        setLoading(true);
        
        const templatesCollectionRef = collection(db, 'defaultTemplates');
        const querySnapshot = await getDocs(templatesCollectionRef);
        
        const templates = [];
        querySnapshot.forEach((doc) => {
          templates.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        setDefaultTemplates(templates);
      } catch (err) {
        console.error('Error fetching default templates:', err);
        setError('Failed to load default templates');
      } finally {
        setLoading(false);
      }
    };

    fetchDefaultTemplates();
  }, [isAdmin, adminLoading]);

  // Create a new default template
  const handleCreateTemplate = async () => {
    if (!isAdmin) {
      setError('Only admins can create default templates');
      return;
    }

    if (!newTemplateName.trim()) {
      setError('Template name cannot be empty');
      return;
    }

    try {
      setLoading(true);
      
      // Create a new template document with a unique ID
      const newTemplate = {
        name: newTemplateName.trim(),
        segments: [],
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Add to Firestore
      const templatesCollectionRef = collection(db, 'defaultTemplates');
      const newDocRef = doc(templatesCollectionRef);
      await setDoc(newDocRef, newTemplate);
      
      // Add to local state with the generated ID
      setDefaultTemplates([...defaultTemplates, { id: newDocRef.id, ...newTemplate }]);
      
      // Reset form
      setNewTemplateName('');
      setShowNewTemplateForm(false);
      setError(null);
    } catch (err) {
      console.error('Error creating template:', err);
      setError('Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  // Edit template name
  const handleEditClick = (index) => {
    setEditIndex(index);
    setEditName(defaultTemplates[index].name);
  };

  const handleSaveEdit = async (index) => {
    if (!editName.trim()) return;
    
    try {
      const updatedTemplate = { ...defaultTemplates[index], name: editName.trim(), updatedAt: new Date() };
      
      // Update in Firestore
      await updateDoc(doc(db, 'defaultTemplates', defaultTemplates[index].id), {
        name: editName.trim(),
        updatedAt: new Date()
      });
      
      // Update local state
      const updatedTemplates = [...defaultTemplates];
      updatedTemplates[index] = updatedTemplate;
      setDefaultTemplates(updatedTemplates);
      
      setEditIndex(null);
      setEditName('');
    } catch (err) {
      console.error('Error updating template:', err);
      setError('Failed to update template');
    }
  };

  const handleCancelEdit = () => {
    setEditIndex(null);
    setEditName('');
  };

  // Delete template
  const handleDeleteClick = (index) => {
    setConfirmationMessage(`Are you sure you want to delete the template "${defaultTemplates[index].name}"? This will remove it for all users.`);
    setConfirmationAction(() => async () => {
      try {
        // Delete from Firestore
        await deleteDoc(doc(db, 'defaultTemplates', defaultTemplates[index].id));
        
        // Update local state
        const updatedTemplates = defaultTemplates.filter((_, i) => i !== index);
        setDefaultTemplates(updatedTemplates);
      } catch (err) {
        console.error('Error deleting template:', err);
        setError('Failed to delete template');
      }
    });
    setShowConfirmation(true);
  };

  // Toggle template expansion
  const toggleTemplateExpand = (index) => {
    if (expandedTemplate === index) {
      setExpandedTemplate(null);
      handleCancelSegmentEdit();
    } else {
      setExpandedTemplate(index);
    }
  };

  // Segment editing
  const handleEditSegmentClick = (templateIndex, segmentIndex, segmentName) => {
    setEditTemplateIndex(templateIndex);
    setEditSegmentIndex(segmentIndex);
    setEditSegmentName(segmentName);
  };

  const handleCancelSegmentEdit = () => {
    setEditTemplateIndex(null);
    setEditSegmentIndex(null);
    setEditSegmentName('');
  };

  const handleSaveSegmentEdit = async (templateIndex, segmentIndex) => {
    if (!editSegmentName.trim()) return;
    
    try {
      const updatedTemplates = [...defaultTemplates];
      updatedTemplates[templateIndex].segments[segmentIndex].option = editSegmentName.trim();
      
      // Update in Firestore
      await updateDoc(doc(db, 'defaultTemplates', defaultTemplates[templateIndex].id), {
        segments: updatedTemplates[templateIndex].segments,
        updatedAt: new Date()
      });
      
      setDefaultTemplates(updatedTemplates);
      handleCancelSegmentEdit();
    } catch (err) {
      console.error('Error updating segment:', err);
      setError('Failed to update segment');
    }
  };

  const handleDeleteSegmentClick = (templateIndex, segmentIndex) => {
    const segmentName = defaultTemplates[templateIndex].segments[segmentIndex].option;
    setConfirmationMessage(`Are you sure you want to delete the segment "${segmentName}"?`);
    setConfirmationAction(() => async () => {
      try {
        const updatedTemplates = [...defaultTemplates];
        updatedTemplates[templateIndex].segments = updatedTemplates[templateIndex].segments.filter((_, i) => i !== segmentIndex);
        
        // Update in Firestore
        await updateDoc(doc(db, 'defaultTemplates', defaultTemplates[templateIndex].id), {
          segments: updatedTemplates[templateIndex].segments,
          updatedAt: new Date()
        });
        
        setDefaultTemplates(updatedTemplates);
      } catch (err) {
        console.error('Error deleting segment:', err);
        setError('Failed to delete segment');
      }
    });
    setShowConfirmation(true);
  };

  const handleAddSegment = async (templateIndex) => {
    // Define a pool of colors to cycle through
    const colorPool = ["#FF3E3E", "#00A1FF", "#FFD700", "#9C27B0", "#4CAF50", "#0071e3", "#2A4759"];
    const segments = defaultTemplates[templateIndex].segments;
    const nextColorIndex = segments.length % colorPool.length;
    const backgroundColor = colorPool[nextColorIndex];
    const textColor = backgroundColor === "#FFD700" ? "#000000" : "#ffffff";
    
    const newSegment = {
      option: "New segment",
      style: {
        backgroundColor,
        textColor
      }
    };
    
    try {
      const updatedTemplates = [...defaultTemplates];
      updatedTemplates[templateIndex].segments.push(newSegment);
      
      // Update in Firestore
      await updateDoc(doc(db, 'defaultTemplates', defaultTemplates[templateIndex].id), {
        segments: updatedTemplates[templateIndex].segments,
        updatedAt: new Date()
      });
      
      setDefaultTemplates(updatedTemplates);
      
      // Automatically start editing the new segment
      const newSegmentIndex = updatedTemplates[templateIndex].segments.length - 1;
      handleEditSegmentClick(templateIndex, newSegmentIndex, "New segment");
    } catch (err) {
      console.error('Error adding segment:', err);
      setError('Failed to add segment');
    }
  };

  // Confirmation dialog handlers
  const handleConfirmYes = () => {
    if (confirmationAction) {
      confirmationAction();
    }
    setShowConfirmation(false);
    setConfirmationAction(null);
  };

  const handleConfirmNo = () => {
    setShowConfirmation(false);
    setConfirmationAction(null);
  };

  // Only show loading on initial load
  if ((adminLoading || loading) && !hasFetchedRef.current) {
    return <div className="admin-templates-loading">Loading...</div>;
  }

  if (!isAdmin) {
    return <div className="admin-templates-error">Only administrators can access this page.</div>;
  }

  return (
    <div className="admin-templates-container">
      <h2 className="admin-templates-title">Manage Default Templates</h2>
      <p className="admin-templates-description">
        These templates will be available to all users. Only administrators can modify them.
      </p>
      
      {error && <div className="admin-templates-error">{error}</div>}
      
      {/* New Template Form */}
      <div className="admin-new-template">
        {showNewTemplateForm ? (
          <div className="admin-new-template-form">
            <input
              type="text"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="Enter template name"
              className="admin-template-input"
            />
            <div className="admin-template-buttons">
              <button 
                className="admin-template-save-btn" 
                onClick={handleCreateTemplate}
                disabled={!newTemplateName.trim()}
              >
                <FontAwesomeIcon icon={faSave} /> Save
              </button>
              <button 
                className="admin-template-cancel-btn" 
                onClick={() => setShowNewTemplateForm(false)}
              >
                <FontAwesomeIcon icon={faTimes} /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <button 
            className="admin-add-template-btn" 
            onClick={() => setShowNewTemplateForm(true)}
          >
            <FontAwesomeIcon icon={faPlus} /> Add New Template
          </button>
        )}
      </div>
      
      {/* Templates List */}
      {defaultTemplates.length === 0 ? (
        <div className="admin-no-templates">No default templates found. Create one to get started.</div>
      ) : (
        <div className="admin-templates-list">
          {defaultTemplates.map((template, index) => (
            <div key={template.id} className="admin-template-item">
              <div className="admin-template-header">
                {editIndex === index ? (
                  <div className="admin-template-edit-form">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="admin-template-edit-input"
                      autoFocus
                    />
                    <div className="admin-template-edit-buttons">
                      <button 
                        className="admin-template-save-btn" 
                        onClick={() => handleSaveEdit(index)}
                        title="Save"
                      >
                        <FontAwesomeIcon icon={faSave} />
                      </button>
                      <button 
                        className="admin-template-cancel-btn" 
                        onClick={handleCancelEdit}
                        title="Cancel"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="admin-template-info">
                      <span className="admin-template-name">{template.name}</span>
                      <span className="admin-template-segments-count">
                        {template.segments.length} {template.segments.length === 1 ? 'segment' : 'segments'}
                      </span>
                    </div>
                    <div className="admin-template-actions">
                      <button 
                        className="admin-template-view-btn" 
                        onClick={() => toggleTemplateExpand(index)}
                        title={expandedTemplate === index ? "Hide" : "View"}
                      >
                        <FontAwesomeIcon icon={expandedTemplate === index ? faEyeSlash : faEye} />
                      </button>
                      <button 
                        className="admin-template-edit-btn" 
                        onClick={() => handleEditClick(index)}
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        className="admin-template-delete-btn" 
                        onClick={() => handleDeleteClick(index)}
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </>
                )}
              </div>
              
              {/* Expanded template content */}
              {expandedTemplate === index && (
                <div className="admin-template-content">
                  <div className="admin-template-content-layout">
                    <div className="admin-template-segments-container">
                      <div className="admin-template-segments-header">
                        <span className="admin-segments-title">Segments:</span>
                        <button 
                          className="admin-add-segment-btn"
                          onClick={() => handleAddSegment(index)}
                          title="Add new segment"
                        >
                          + Add
                        </button>
                      </div>
                      <div className="admin-template-segments-list">
                        {template.segments.map((segment, segIndex) => (
                          <div key={segIndex} className="admin-template-segment-item">
                            <div 
                              className="admin-segment-color"
                              style={{ backgroundColor: segment.style.backgroundColor }}
                            ></div>
                            {editSegmentIndex === segIndex && editTemplateIndex === index ? (
                              <div className="admin-segment-edit-form">
                                <input
                                  type="text"
                                  value={editSegmentName}
                                  onChange={(e) => setEditSegmentName(e.target.value)}
                                  className="admin-segment-edit-input"
                                  autoFocus
                                />
                                <div className="admin-segment-edit-buttons">
                                  <button 
                                    className="admin-segment-save-btn" 
                                    onClick={() => handleSaveSegmentEdit(index, segIndex)}
                                    title="Save"
                                  >
                                    <FontAwesomeIcon icon={faSave} />
                                  </button>
                                  <button 
                                    className="admin-segment-cancel-btn" 
                                    onClick={handleCancelSegmentEdit}
                                    title="Cancel"
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <span className="admin-segment-name">{segment.option}</span>
                                <div className="admin-segment-actions">
                                  <button 
                                    className="admin-segment-edit-btn" 
                                    onClick={() => handleEditSegmentClick(index, segIndex, segment.option)}
                                    title="Edit"
                                  >
                                    <FontAwesomeIcon icon={faEdit} />
                                  </button>
                                  <button 
                                    className="admin-segment-delete-btn" 
                                    onClick={() => handleDeleteSegmentClick(index, segIndex)}
                                    title="Delete"
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Confirmation Dialog */}
      <div className={`admin-confirmation-overlay ${showConfirmation ? 'visible' : ''}`}>
        <div className="admin-confirmation-dialog">
          <p className="admin-confirmation-message">{confirmationMessage}</p>
          <div className="admin-confirmation-buttons">
            <button className="admin-confirm-yes-btn" onClick={handleConfirmYes}>Yes</button>
            <button className="admin-confirm-no-btn" onClick={handleConfirmNo}>No</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTemplates;